import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-secret-reveal.css?inline';
import '../pk-token-reveal/pk-token-reveal.js';
import '../pk-button/pk-button.js';

/**
 * `<pk-secret-reveal>` — shows a secret **once**: a recovered private key, a one-time recovery approval token.
 * Extends `<pk-token-reveal>` (it renders one for the copy / show-hide surface) with a **Download** button and a
 * **Done** button that wipes the secret from the page: after that it cannot be shown again.
 *
 * The secret lives in a JS property, not in the DOM. A server-rendered `secret` attribute is read once on connect
 * and removed from the element at once, so it does not linger in the markup.
 *
 * Attributes:
 *   - `secret`    — the secret (read once, then removed; prefer the `secret` property / `show()`).
 *   - `label`     — heading (default `Your secret`).
 *   - `warning`   — the callout (default `Shown once — save it now. It is wiped from this page when you close it.`).
 *   - `filename`  — download name (default `secret.txt`); empty hides Download.
 *   - `mime`      — download type (default `text/plain`).
 *   - `masked`    — start masked.
 *
 * Methods: `show(secret)`, `wipe()`, `download()`.
 *
 * Events:
 *   - `pk-secret-downloaded` — `{ filename }` after a download.
 *   - `pk-secret-closed`     — after the Done button (or `wipe()`) cleared the secret.
 *   - `pk-token-copied`      — from the inner `<pk-token-reveal>` on copy (detail carries the secret; handle it with care).
 */
export class PkSecretReveal extends HTMLElement {
    static readonly tagName = 'pk-secret-reveal';

    static get observedAttributes(): string[] {
        return ['secret', 'label', 'warning', 'filename', 'mime', 'masked'];
    }

    private readonly _root: ShadowRoot;
    private _secret = '';
    private _wiped = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._absorbAttribute();
        this._render();
    }

    attributeChangedCallback(name: string): void {
        if (name === 'secret') {
            this._absorbAttribute();
        }
        if (this.isConnected) this._render();
    }

    /** The secret while shown ('' once wiped). */
    get secret(): string {
        return this._secret;
    }

    set secret(value: string) {
        this.show(value);
    }

    /** True once the secret has been wiped (it cannot be shown again). */
    get wiped(): boolean {
        return this._wiped;
    }

    /** Shows a secret (no-op once wiped). */
    show(secret: string): void {
        if (this._wiped) return;
        this._secret = typeof secret === 'string' ? secret : '';
        if (this.isConnected) this._render();
    }

    /** Clears the secret from the page for good. */
    wipe(): void {
        const had = this._secret !== '' || !this._wiped;
        this._secret = '';
        this._wiped = true;
        this._render();
        if (had) this.dispatchEvent(new CustomEvent('pk-secret-closed', { bubbles: true, composed: true }));
    }

    /** Downloads the secret as a file. Returns false when there is nothing to download or no Blob URL support. */
    download(): boolean {
        const filename = this._filename;
        if (!this._secret || !filename || typeof URL.createObjectURL !== 'function') return false;
        const blob = new Blob([this._secret], { type: this.getAttribute('mime') || 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener';
        a.style.display = 'none';
        this._root.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        this.dispatchEvent(new CustomEvent('pk-secret-downloaded', { bubbles: true, composed: true, detail: { filename } }));
        return true;
    }

    private get _filename(): string {
        const f = this.getAttribute('filename');
        return f === null ? 'secret.txt' : f;
    }

    private _absorbAttribute(): void {
        const value = this.getAttribute('secret');
        if (value === null) return;
        this.removeAttribute('secret');
        this.show(value);
    }

    private readonly _onDownload = (): void => { this.download(); };
    private readonly _onDone = (): void => { this.wipe(); };

    private template(): TemplateResult {
        if (this._wiped) {
            return html`<style>${styles}</style><p class="wiped" role="status">The secret was cleared from this page.</p>`;
        }
        if (!this._secret) return html`<style>${styles}</style>`;
        const filename = this._filename;
        return html`
            <style>${styles}</style>
            <pk-token-reveal
                class="reveal"
                token=${this._secret}
                label=${this.getAttribute('label') ?? 'Your secret'}
                warning=${this.getAttribute('warning') ?? 'Shown once — save it now. It is wiped from this page when you close it.'}
                ?masked=${this.hasAttribute('masked')}
            ></pk-token-reveal>
            <div class="actions">
                ${filename ? html`<pk-button class="download" variant="secondary" type="button" @click=${this._onDownload}>Download</pk-button>` : nothing}
                <pk-button class="done" variant="primary" type="button" @click=${this._onDone}>Done — wipe it</pk-button>
            </div>
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkSecretReveal.tagName)) {
    customElements.define(PkSecretReveal.tagName, PkSecretReveal);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-secret-reveal': PkSecretReveal;
    }
}
